import React, { useState } from 'react';
import { Button, Space } from 'antd';
import {
  DownOutlined,
  UpOutlined,
  ReloadOutlined,
  LoadingOutlined,
  CloseOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export type DownloadStatus =
  | 'preparing'
  | 'processing'
  | 'downloading'
  | 'completed'
  | 'error';

interface FileDownload {
  id: string;
  name: string;
  url: string;
  progress: number;
  status: DownloadStatus;
  note?: string;
}

interface DownloadProgressPanelProps {
  files: FileDownload[];
  onCancel?: () => void;
  onRetry?: (id: string) => void;
  onCancelItem?: (id: string) => void;
}

// The zip is built by a background job and then fetched by the browser itself,
// so there is no byte-level progress to show — each entry reports a phase
// (queued, zipping, handed to the browser) instead of a percentage.
const DownloadProgressPanel: React.FC<DownloadProgressPanelProps> = ({
  files,
  onRetry,
  onCancelItem
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  const allComplete = files.every(f => f.status === 'completed');

  if (files.length === 0) return null;

  // Number of downloads still being prepared — shown in the collapsed pill.
  const activeCount = files.filter(
    f => f.status !== 'completed' && f.status !== 'error'
  ).length;

  // Nothing left running, but a failed entry is still waiting for a retry.
  const isIdleWithError =
    activeCount === 0 && files.some(f => f.status === 'error');

  const pillLabel = isIdleWithError
    ? t('Download failed')
    : allComplete
      ? t('Download Complete')
      : `${t('Downloading')} (${activeCount})`;

  // Collapsed: show a compact pill with the active count, click to expand.
  if (collapsed) {
    return (
      <div
        onClick={() => setCollapsed(false)}
        className="fixed bottom-6 right-6 z-[9999] flex items-center gap-2 cursor-pointer
                   bg-[var(--neutral-950)] border border-[#26324a] rounded-lg shadow-xl
                   px-4 py-3 transition-all duration-300 hover:border-[#3a4a68]"
      >
        {activeCount > 0 && <LoadingOutlined spin className="text-cyan-400" />}
        <span className="text-sm font-semibold text-[#F5F6FA]">{pillLabel}</span>
        <UpOutlined className="!text-slate-400 text-xs" />
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] w-96 max-h-[500px] overflow-auto
                 bg-[var(--neutral-950)] border border-[#26324a] rounded-lg shadow-xl
                 p-4 transition-all duration-300"
    >
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-[#F5F6FA]">
          {allComplete ? t('Download Complete') : t('Downloads')}
        </h3>
        <Button
          type="text"
          icon={<DownOutlined />}
          onClick={() => setCollapsed(true)}
          className="!text-slate-400 hover:!text-white"
        />
      </div>

      <div className="space-y-3">
        {files.map(file => {
          const isError = file.status === 'error';
          const isCompleted = file.status === 'completed';
          // Everything before the browser takes over: queued, zipping, or the
          // client-side exports (AOI) that come through as 'downloading'.
          const isBusy = !isError && !isCompleted;

          return (
            <div key={file.id} className="text-sm bg-[var(--neutral-900)] p-3 rounded">
              <div className="flex justify-between items-start mb-2">
                <span
                  className="text-slate-300 truncate flex-1 mr-2 leading-tight"
                  title={file.name}
                >
                  {file.name}
                </span>
                <Space size={4}>
                  {isError && onRetry && (
                    <Button
                      type="text"
                      size="small"
                      title={t('Retry')}
                      icon={<ReloadOutlined />}
                      onClick={() => onRetry(file.id)}
                      className="!text-slate-300 hover:!text-white !p-1"
                    />
                  )}
                  {!isCompleted && onCancelItem && (
                    <Button
                      type="text"
                      size="small"
                      title={t('Cancel')}
                      icon={<CloseOutlined />}
                      onClick={() => onCancelItem(file.id)}
                      className="!text-slate-400 hover:!text-red-400 !p-1"
                    />
                  )}
                </Space>
              </div>

              {isBusy && (
                <div className="flex items-center gap-2 py-1 text-cyan-400 text-xs">
                  <LoadingOutlined spin />
                  <span>
                    {file.status === 'processing'
                      ? t('Compressing data')
                      : t('Preparing download data')}
                    …
                  </span>
                </div>
              )}
              {isCompleted && (
                <div className="flex items-center gap-2 py-1 text-emerald-400 text-xs">
                  <CheckCircleOutlined />
                  <span>{t('Download started')}</span>
                </div>
              )}
              {isError && (
                <div className="py-1 text-red-400 text-xs break-words">
                  {file.note || t('Download failed')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DownloadProgressPanel;
